/**
 * いま PJ を見ているかどうかを、画面の中身を知らない部品（サイドバー・ヘッダー・ノードのリンク）
 * から読む。
 *
 * PJ は個々の画面の検索条件だが、**PJ を選んだ状態はアプリ全体の状態として振る舞う**。
 * PJ サマリーから業務詳細へ入り、そこからサイドバーで画面一覧へ移る、という動きの間ずっと
 * 同じ PJ を見続けられなければ「PJ を選ぶ」という操作が成立しない。外側の部品が現在の PJ を
 * 知る必要があるのはそのためである。
 *
 * **PJ を prop で配らないのはこのためでもある。** 「PJ を引き継ぐ」判断を呼び出し側に置くと、
 * 渡し忘れが型でも lint でも落ちず、その導線だけ黙ってマスターへ落ちる。実際そうなっていた —
 * ノードのリンクは PJ を任意の prop で受けており、9 箇所のうち 7 箇所が渡していなかった。
 * 現在地から読めば、忘れるという選択肢そのものが無くなる。
 *
 * 読む場所はルーターの現在地 1 つに閉じる。別途 state を置いて持ち回ると、URL と state の
 * 2 つが同じ事実を表すことになり、深いリンクで開いたときや戻る操作で必ず食い違う。
 *
 * **PJ 番号があることと、PJ モードであることは同じではない。** PJ を読まない画面
 * （PJ 一覧など）でも URL に `?pj=` は書ける。番号の有無だけで案内を出すと、PJ を
 * 適用していない画面が「PJ を適用した後の姿」だと読める。現在地が PJ を読むルートかどうかを
 * 併せて見る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いま見ている PJ 番号と、その画面が PJ を読むかどうかをルーターの現在地から読む。
 * @graph-connects currentPjOf [calls] 検索条件とパスから PJ 番号を畳む
 * @graph-connects readsPj [calls] 現在地が PJ を読むルートかの判定
 * @graph-connects pjExitTarget [calls] PJ を外した後の行き先
 */
import { useRouterState } from '@tanstack/react-router';

import { readsPj, type PjModeRoute } from '../routes/route-paths.js';
import { pjExitTarget, type PjExitTarget } from '../routes/pj-exit.js';
import { currentPjOf } from '../routes/pj-search.js';

/**
 * いま見ている PJ 番号を返す。PJ を選んでいなければ `undefined`。
 *
 * 検索条件（`?pj=`）とパスの親（`/pjs/$pjNumber/...`）の**どちらに置かれた PJ も
 * 読む**。検索条件だけを読むと、PJ モードの入口である PJ サマリーに立っている間だけ PJ が
 * 消え、そこから移った先でマスターへ落ちる（畳み方は `currentPjOf`）。
 *
 * @returns PJ 番号。PJ を選んでいなければ `undefined`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ルーターの現在地から、検索条件とパスのどちらに置かれた PJ も読む。
 * @graph-connects currentPjOf [calls] 検索条件とパスから PJ 番号を畳む
 */
export function useCurrentPj(): string | undefined {
  return useRouterState({
    select: (state) =>
      currentPjOf(state.location.search as Record<string, unknown>, mergedParamsOf(state)),
  });
}

/**
 * 現在地の全一致からパスパラメータを 1 つに畳む。
 *
 * 末端の一致だけを見る形にすると「一致が 1 つも無い」という起こらない場合の分岐が残り、その枝を
 * 通すためだけのテストが要る。重ねる形なら分岐そのものが無くなる。親から子へ順に重ねるので、
 * 同じ名前があれば子の値が勝つ。
 *
 * @param state ルーターの現在地
 * @returns 現在地のパスパラメータ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 現在地の全一致のパスパラメータを 1 つのオブジェクトへ重ねる。
 * @graph-connects none
 */
function mergedParamsOf(state: {
  matches: readonly { params: unknown }[];
}): Record<string, unknown> {
  return state.matches.reduce<Record<string, unknown>>(
    (merged, match) => ({ ...merged, ...(match.params as Record<string, unknown>) }),
    {},
  );
}

/**
 * いまの画面が PJ を読むなら、PJ を外した後の行き先を返す。PJ を読まない画面では `undefined`。
 *
 * 「PJ モードかどうか」と「外したらどこへ行くか」を 1 つの答えで返すのは、その 2 つが同じ
 * 事実（現在地が PJ を読むルートか）から出るからである。別々に取ると、案内は出るのに外す口が
 * 無い現在地や、その逆が型の上で表現できてしまう。
 *
 * 実 URL（`/screens/apple-auth-callback`）ではなくルートの定義パス（`/screens/$screenId`）で
 * 判定する。実 URL で突き合わせるとパスパラメータの値ごとに判定が要り、ID に `/` を含む
 * ノード（`cons/styling-proposal` など）で必ず外れる。
 *
 * 末端の一致だけを見ずに全一致を重ねるのは、`mergedParamsOf` と同じ理由である — 末端を取る形は
 * 「一致が 1 つも無い」という起こらない場合の分岐を残し、その枝を通すためだけのテストが要る。
 *
 * @returns PJ を外した後の行き先。PJ を読まない画面なら `undefined`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いまの画面が PJ を読むかを判定し、読むなら PJ を外した後の行き先を返す。
 * @graph-connects readsPj [calls] PJ を読むルートかの判定
 * @graph-connects pjExitTarget [calls] PJ を外した後の行き先
 */
export function usePjModeExit(): PjExitTarget | undefined {
  return useRouterState({
    select: (state) => {
      const route = state.matches.reduce<PjModeRoute | undefined>(
        (found, match) => (readsPj(match.routeId) ? match.routeId : found),
        undefined,
      );

      return route === undefined ? undefined : pjExitTarget(route, mergedParamsOf(state));
    },
  });
}
